// ─────────────────────────────────────────────────────────────────────────────
// i18n bootstrap (react-i18next + i18next + browser language detection)
//
// Scalable, namespace-based setup. To add UI strings:
//   1. Add the key to web/src/i18n/locales/en/<namespace>.json (the source of truth)
//   2. Mirror the same key in fr / sw / ln
//   3. In a component:  const { t } = useTranslation('<namespace>');  …  t('key')
//
// To add a NEW namespace: create locales/<lng>/<name>.json for every language,
// import the four files below, register them in `resources`, and add the name to
// the `ns` array. Nothing else needs to change.
//
// To add a NEW language: create locales/<code>/*.json, import + register them,
// and add the code to `supportedLngs` and to SUPPORTED_LANGUAGES below.
// ─────────────────────────────────────────────────────────────────────────────
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// English (source of truth)
import enCommon from './locales/en/common.json';
import enNav from './locales/en/nav.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enCourses from './locales/en/courses.json';
import enQuizzes from './locales/en/quizzes.json';
import enAssignments from './locales/en/assignments.json';
import enGrades from './locales/en/grades.json';
import enAcademics from './locales/en/academics.json';
import enDiscussions from './locales/en/discussions.json';
import enSettings from './locales/en/settings.json';
import enAdmin from './locales/en/admin.json';
import enBilling from './locales/en/billing.json';
import enCertificates from './locales/en/certificates.json';
import enEnrollment from './locales/en/enrollment.json';
import enAttendance from './locales/en/attendance.json';
import enNotifications from './locales/en/notifications.json';
import enBookmarks from './locales/en/bookmarks.json';
import enAdvising from './locales/en/advising.json';
import enSubmissions from './locales/en/submissions.json';
import enInstructor from './locales/en/instructor.json';
import enLearning from './locales/en/learning.json';
import enHelp from './locales/en/help.json';
import enResearch from './locales/en/research.json';
import enUniversities from './locales/en/universities.json';
import enLive from './locales/en/live.json';
// Public marketing site — English + French only (sw/ln fall back to en).
import enMarketing from './locales/en/marketing.json';
import enProduct from './locales/en/product.json';
import enCompany from './locales/en/company.json';
import enResources from './locales/en/resources.json';

// French
import frCommon from './locales/fr/common.json';
import frNav from './locales/fr/nav.json';
import frAuth from './locales/fr/auth.json';
import frDashboard from './locales/fr/dashboard.json';
import frCourses from './locales/fr/courses.json';
import frQuizzes from './locales/fr/quizzes.json';
import frAssignments from './locales/fr/assignments.json';
import frGrades from './locales/fr/grades.json';
import frAcademics from './locales/fr/academics.json';
import frDiscussions from './locales/fr/discussions.json';
import frSettings from './locales/fr/settings.json';
import frAdmin from './locales/fr/admin.json';
import frBilling from './locales/fr/billing.json';
import frCertificates from './locales/fr/certificates.json';
import frEnrollment from './locales/fr/enrollment.json';
import frAttendance from './locales/fr/attendance.json';
import frNotifications from './locales/fr/notifications.json';
import frBookmarks from './locales/fr/bookmarks.json';
import frAdvising from './locales/fr/advising.json';
import frSubmissions from './locales/fr/submissions.json';
import frInstructor from './locales/fr/instructor.json';
import frLearning from './locales/fr/learning.json';
import frHelp from './locales/fr/help.json';
import frResearch from './locales/fr/research.json';
import frUniversities from './locales/fr/universities.json';
import frLive from './locales/fr/live.json';
import frMarketing from './locales/fr/marketing.json';
import frProduct from './locales/fr/product.json';
import frCompany from './locales/fr/company.json';
import frResources from './locales/fr/resources.json';

// Kiswahili (Swahili)
import swCommon from './locales/sw/common.json';
import swNav from './locales/sw/nav.json';
import swAuth from './locales/sw/auth.json';
import swDashboard from './locales/sw/dashboard.json';
import swCourses from './locales/sw/courses.json';
import swQuizzes from './locales/sw/quizzes.json';
import swAssignments from './locales/sw/assignments.json';
import swGrades from './locales/sw/grades.json';
import swAcademics from './locales/sw/academics.json';
import swDiscussions from './locales/sw/discussions.json';
import swSettings from './locales/sw/settings.json';
import swAdmin from './locales/sw/admin.json';
import swBilling from './locales/sw/billing.json';
import swCertificates from './locales/sw/certificates.json';
import swEnrollment from './locales/sw/enrollment.json';
import swAttendance from './locales/sw/attendance.json';
import swNotifications from './locales/sw/notifications.json';
import swBookmarks from './locales/sw/bookmarks.json';
import swAdvising from './locales/sw/advising.json';
import swSubmissions from './locales/sw/submissions.json';
import swInstructor from './locales/sw/instructor.json';
import swLearning from './locales/sw/learning.json';
import swHelp from './locales/sw/help.json';
import swResearch from './locales/sw/research.json';
import swUniversities from './locales/sw/universities.json';
import swLive from './locales/sw/live.json';

// Lingala
import lnCommon from './locales/ln/common.json';
import lnNav from './locales/ln/nav.json';
import lnAuth from './locales/ln/auth.json';
import lnDashboard from './locales/ln/dashboard.json';
import lnCourses from './locales/ln/courses.json';
import lnQuizzes from './locales/ln/quizzes.json';
import lnAssignments from './locales/ln/assignments.json';
import lnGrades from './locales/ln/grades.json';
import lnAcademics from './locales/ln/academics.json';
import lnDiscussions from './locales/ln/discussions.json';
import lnSettings from './locales/ln/settings.json';
import lnAdmin from './locales/ln/admin.json';
import lnBilling from './locales/ln/billing.json';
import lnCertificates from './locales/ln/certificates.json';
import lnEnrollment from './locales/ln/enrollment.json';
import lnAttendance from './locales/ln/attendance.json';
import lnNotifications from './locales/ln/notifications.json';
import lnBookmarks from './locales/ln/bookmarks.json';
import lnAdvising from './locales/ln/advising.json';
import lnSubmissions from './locales/ln/submissions.json';
import lnInstructor from './locales/ln/instructor.json';
import lnLearning from './locales/ln/learning.json';
import lnHelp from './locales/ln/help.json';
import lnResearch from './locales/ln/research.json';
import lnUniversities from './locales/ln/universities.json';
import lnLive from './locales/ln/live.json';

// Single source for the language switcher UI. `nativeName` is shown to users in
// their OWN language (best practice: never force a user to recognise their
// language written in a script/word they don't read).
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English',   nativeName: 'English',   flag: '🇬🇧' },
  { code: 'fr', label: 'French',    nativeName: 'Français',  flag: '🇫🇷' },
  { code: 'sw', label: 'Kiswahili', nativeName: 'Kiswahili', flag: '🇹🇿' },
  { code: 'ln', label: 'Lingala',   nativeName: 'Lingála',   flag: '🇨🇩' },
];

export const DEFAULT_LANGUAGE = 'en';

const resources = {
  en: { common: enCommon, nav: enNav, auth: enAuth, dashboard: enDashboard, courses: enCourses, quizzes: enQuizzes, assignments: enAssignments, grades: enGrades, academics: enAcademics, discussions: enDiscussions, settings: enSettings, admin: enAdmin, billing: enBilling, certificates: enCertificates, enrollment: enEnrollment, attendance: enAttendance, notifications: enNotifications, bookmarks: enBookmarks, advising: enAdvising, submissions: enSubmissions, instructor: enInstructor, learning: enLearning, help: enHelp, research: enResearch, universities: enUniversities, live: enLive, marketing: enMarketing, product: enProduct, company: enCompany, resources: enResources },
  fr: { common: frCommon, nav: frNav, auth: frAuth, dashboard: frDashboard, courses: frCourses, quizzes: frQuizzes, assignments: frAssignments, grades: frGrades, academics: frAcademics, discussions: frDiscussions, settings: frSettings, admin: frAdmin, billing: frBilling, certificates: frCertificates, enrollment: frEnrollment, attendance: frAttendance, notifications: frNotifications, bookmarks: frBookmarks, advising: frAdvising, submissions: frSubmissions, instructor: frInstructor, learning: frLearning, help: frHelp, research: frResearch, universities: frUniversities, live: frLive, marketing: frMarketing, product: frProduct, company: frCompany, resources: frResources },
  sw: { common: swCommon, nav: swNav, auth: swAuth, dashboard: swDashboard, courses: swCourses, quizzes: swQuizzes, assignments: swAssignments, grades: swGrades, academics: swAcademics, discussions: swDiscussions, settings: swSettings, admin: swAdmin, billing: swBilling, certificates: swCertificates, enrollment: swEnrollment, attendance: swAttendance, notifications: swNotifications, bookmarks: swBookmarks, advising: swAdvising, submissions: swSubmissions, instructor: swInstructor, learning: swLearning, help: swHelp, research: swResearch, universities: swUniversities, live: swLive },
  ln: { common: lnCommon, nav: lnNav, auth: lnAuth, dashboard: lnDashboard, courses: lnCourses, quizzes: lnQuizzes, assignments: lnAssignments, grades: lnGrades, academics: lnAcademics, discussions: lnDiscussions, settings: lnSettings, admin: lnAdmin, billing: lnBilling, certificates: lnCertificates, enrollment: lnEnrollment, attendance: lnAttendance, notifications: lnNotifications, bookmarks: lnBookmarks, advising: lnAdvising, submissions: lnSubmissions, instructor: lnInstructor, learning: lnLearning, help: lnHelp, research: lnResearch, universities: lnUniversities, live: lnLive },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    // Treat region variants (e.g. "fr-FR", "sw-KE") as their base language.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,

    ns: ['common', 'nav', 'auth', 'dashboard', 'courses', 'quizzes', 'assignments', 'grades', 'academics', 'discussions', 'settings', 'admin', 'billing', 'certificates', 'enrollment', 'attendance', 'notifications', 'bookmarks', 'advising', 'submissions', 'instructor', 'learning', 'help', 'research', 'universities', 'live', 'marketing', 'product', 'company', 'resources'],
    defaultNS: 'common',

    detection: {
      // Persist the user's explicit choice; fall back to the browser locale.
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'vitrine_lang',
      caches: ['localStorage'],
    },

    interpolation: {
      // React already escapes against XSS.
      escapeValue: false,
    },

    // Missing keys render their fallback-language value rather than the raw key.
    returnEmptyString: false,
    react: {
      useSuspense: false, // resources are bundled, so no async load to suspend on
    },
  });

// Keep <html lang="…"> in sync for accessibility / SEO.
i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', lng);
  }
});

export default i18n;
